// HUD: år og årstid, kroner, grøn andel med mål, tillid og fart. Store tal, små ord.
// Farve er aldrig det eneste signal: tal og ikoner står altid ved siden af.

import type { Game } from '../game';
import { greenGoal, greenPct, SEASON_NAMES, TICK_HZ, YEAR_TICKS, levelTicks, trustMax } from '../sim';
import type { State } from '../sim';
import { check } from '../sim/actions';
import { isPowered } from '../sim/cities';
import { earlyBonusPct } from '../sim/economy';
import { hasDelivery } from '../sim/state';
import { calendarYear } from '../sim/year';
import { cityLook } from '../render/look';
import { h, setAttr, setText, toggle } from './dom';
import { ICON } from './icons';
import { kr, krShort, plural } from './text';

export interface HudHandlers {
  pause(): void;
  speed(): void;
  mute(): void;
  menu(): void;
  callYear(): void;
  /** Lad AI spille, eller tag over igen. */
  ai(): void;
}

export class Hud {
  readonly el: HTMLElement;
  private year = h('b', { class: 'kpi-num' });
  private season = h('span', { class: 'kpi-label' });
  private yearBar = h('i', { class: 'bar-fill' });
  private money = h('b', { class: 'kpi-num' });
  private moneyCell: HTMLElement;
  private green = h('b', { class: 'kpi-num' });
  private greenBar = h('i', { class: 'bar-fill green' });
  private greenGoal = h('i', { class: 'bar-goal' });
  private greenLabel = h('span', { class: 'kpi-label' });
  private hearts = h('span', { class: 'hearts' });
  private trustLabel = h('span', { class: 'kpi-label' }, 'Tillid');
  private pauseBtn: HTMLButtonElement;
  private speedBtn: HTMLButtonElement;
  private muteBtn: HTMLButtonElement;
  private callBtn: HTMLButtonElement;
  private aiBtn: HTMLButtonElement;
  /** Kan AI'en overtage lige nu (solo, ikke online, ikke Volt)? Sættes af main før update. */
  aiCan = false;
  /** Spiller AI'en lige nu? */
  aiOn = false;
  /** Auto-spol kører: en lille turkis pille ved fartknappen. */
  // Skærmlæsere får fartknappens etiket; pillen er kun et synligt tegn på knappens hjørne.
  private spolPill = h('span', { class: 'spol-pill', hidden: true, 'aria-hidden': 'true' }, '×3');
  private lastMoney = -1;
  /** Kassens viste beløb: ruller op mod det rigtige, når mønterne er landet. Fald vises med det samme. */
  private shownMoney = -1;
  private rollFrom = 0;
  private rollAt = 0;
  private holdUntil = 0;
  /** Rolig bevægelse: tallet blinker i stedet for at rulle. */
  calm = false;
  private heartsKey = '';
  private riskKey = '';

  constructor(handlers: HudHandlers) {
    this.pauseBtn = h('button', { type: 'button', class: 'icon-btn', 'aria-label': 'Pause', onclick: () => handlers.pause() });
    this.speedBtn = h('button', { type: 'button', class: 'icon-btn speed', 'aria-label': 'Dobbelt fart', onclick: () => handlers.speed() });
    this.muteBtn = h('button', { type: 'button', class: 'icon-btn', 'aria-label': 'Slå lyden fra', onclick: () => handlers.mute() });
    const menuBtn = h('button', { type: 'button', class: 'icon-btn home', 'aria-label': 'Menu', html: ICON.home, onclick: () => handlers.menu() });
    this.callBtn = h('button', { type: 'button', class: 'chip call', hidden: true, onclick: () => handlers.callYear() });
    this.aiBtn = h('button', { type: 'button', class: 'icon-btn ai', hidden: true, onclick: () => handlers.ai() });
    // Genveje: kun title og aria-keyshortcuts, for knapperne skifter indhold hver gang.
    setAttr(this.pauseBtn, 'aria-keyshortcuts', 'P Space');
    setAttr(this.pauseBtn, 'title', 'Pause (P eller mellemrum)');
    setAttr(this.speedBtn, 'aria-keyshortcuts', 'F');
    setAttr(this.speedBtn, 'title', 'Dobbelt fart (F)');
    setAttr(this.callBtn, 'aria-keyshortcuts', 'N');
    setAttr(this.aiBtn, 'aria-keyshortcuts', 'A');
    this.moneyCell = h('div', { class: 'kpi money' }, h('span', { class: 'kpi-row', html: ICON.coin }, this.money), h('span', { class: 'kpi-label' }, 'Kassen'));
    this.el = h(
      'header',
      { class: 'hud', id: 'hud' },
      h(
        'div',
        { class: 'kpis' },
        h('div', { class: 'kpi year' }, h('span', { class: 'kpi-row' }, this.year), this.season, h('span', { class: 'bar', 'aria-hidden': 'true' }, this.yearBar)),
        this.moneyCell,
        h(
          'div',
          { class: 'kpi green' },
          h('span', { class: 'kpi-row', html: ICON.leaf }, this.green),
          this.greenLabel,
          h('span', { class: 'bar', 'aria-hidden': 'true' }, this.greenBar, this.greenGoal),
        ),
        h('div', { class: 'kpi trust' }, h('span', { class: 'kpi-row' }, this.hearts), this.trustLabel),
      ),
      h('div', { class: 'controls' }, this.callBtn, this.aiBtn, this.pauseBtn, this.speedBtn, this.muteBtn, menuBtn),
    );
  }

  /** En lille gul mærkat under kassen, der stiger op og forsvinder: "+6 kr løn". */
  gain(text: string): void {
    const kpis = this.moneyCell.parentElement;
    if (!kpis) return;
    const chip = h('span', { class: 'kpi-gain', 'aria-hidden': 'true' }, text);
    chip.style.left = `${Math.round(kpis.offsetLeft + this.moneyCell.offsetLeft + this.moneyCell.offsetWidth / 2)}px`;
    chip.style.top = `${Math.round(kpis.offsetTop + kpis.offsetHeight + 2)}px`;
    this.el.append(chip);
    setTimeout(() => chip.remove(), 2000);
  }

  /** Midten af kassens mønt på skærmen: der lander de flyvende mønter. */
  coinTarget(): { x: number; y: number } | null {
    const svg = this.moneyCell.querySelector('svg') ?? this.moneyCell;
    const r = svg.getBoundingClientRect();
    return r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }

  /** Vent `ms` med at rulle kassen op (mønterne er på vej). */
  holdRoll(ms: number): void {
    this.holdUntil = Math.max(this.holdUntil, performance.now() + ms);
  }

  /** Det beløb, kassen viser lige nu (til test). */
  get moneyShown(): number {
    return this.shownMoney;
  }

  private rollMoney(budget: number): void {
    const now = performance.now();
    if (this.shownMoney < 0 || budget < this.shownMoney || this.calm) {
      if (this.calm && this.shownMoney >= 0 && budget > this.shownMoney) this.bump('blink');
      this.shownMoney = budget;
      this.rollAt = 0;
    } else if (budget > this.shownMoney && now >= this.holdUntil) {
      if (this.rollAt === 0) {
        this.rollFrom = this.shownMoney;
        this.rollAt = now;
        this.bump('bump-up');
      }
      const f = Math.min(1, (now - this.rollAt) / 250);
      const e = 1 - (1 - f) * (1 - f);
      this.shownMoney = f >= 1 ? budget : Math.max(this.shownMoney, Math.round(this.rollFrom + (budget - this.rollFrom) * e));
      if (this.shownMoney >= budget) this.rollAt = 0;
    }
    // Dansk tusindtalsskiller; over 100.000 kr forkortet, med det fulde beløb i title.
    const short = krShort(this.shownMoney);
    setText(this.money, short);
    toggle(this.money, 'long', short.length > 7);
    setAttr(this.moneyCell, 'title', short === kr(this.shownMoney) ? null : `${kr(this.shownMoney)} i kassen`);
  }

  private bump(cls: 'bump-up' | 'bump-down' | 'blink'): void {
    this.moneyCell.classList.remove('bump-up', 'bump-down', 'blink');
    void this.moneyCell.offsetWidth;
    this.moneyCell.classList.add(cls);
  }

  update(game: Game, muted: boolean): void {
    const s = game.state;
    const lv = game.level;
    // År og årstid.
    if (s.finale) {
      setText(this.year, 'Finale');
      setText(this.season, 'Hold ud');
      const total = levelTicks(lv) - lv.years * YEAR_TICKS;
      const f = Math.min(1, (s.clockTick - lv.years * YEAR_TICKS) / Math.max(1, total));
      this.yearBar.style.transform = `scaleX(${f.toFixed(3)})`;
    } else {
      setText(this.year, String(calendarYear(lv, s.year)));
      const season = SEASON_NAMES[Math.floor((s.clockTick % YEAR_TICKS) / (YEAR_TICKS / 4))] ?? '';
      setText(this.season, season);
      const f = (s.clockTick % YEAR_TICKS) / YEAR_TICKS;
      this.yearBar.style.transform = `scaleX(${(s.phase === 'yearend' ? 1 : f).toFixed(3)})`;
    }
    // Kroner, med et lille hop når beløbet ændrer sig.
    // Op ruller tallet (bump ved start), ned hopper det med det samme.
    if (this.lastMoney >= 0 && s.budget < this.lastMoney) this.bump('bump-down');
    this.rollMoney(s.budget);
    this.lastMoney = s.budget;
    // Grøn andel og mål.
    // Uden levering er der ingen andel at vise, og mens byer mangler strøm siger feltet det
    // i stedet for at prale med 100 % fra den ene by, der har strøm.
    const any = hasDelivery(s);
    const g = any ? Math.round(greenPct(s)) : 0;
    const goal = greenGoal(lv.greenGoalPct, s.difficulty);
    const short = starving(s);
    setText(this.green, any ? `${g} %` : '–');
    // Kort nok til den smalleste celle (telefon på siden); den fulde sætning står i title.
    // IGN runde 2: "2 venter" under grøn og "1 i sort" under hjerterne skiftede plads og var uklare.
    // Grøn viser altid målet; byer i nød tælles ét sted, under hjerterne.
    setText(this.greenLabel, `Mål ${goal} %`);
    setAttr(this.greenLabel, 'title', `Mål for grøn strøm: ${goal} %`);
    toggle(this.green, 'dim', !any || short > 0);
    setAttr(this.green, 'aria-label', any ? `Grøn strøm ${g} procent, mål ${goal} procent` : `Ingen strøm leveret endnu, mål ${goal} procent`);
    this.greenBar.style.transform = `scaleX(${Math.min(100, g) / 100})`;
    this.greenGoal.style.left = `${goal}%`;
    toggle(this.greenBar, 'met', any && short === 0 && g >= goal);
    // Byer i sort står under hjerterne: det er dem, der koster tillid.
    let out = 0;
    for (const n of activeCities(s)) if (n.city?.out) out++;
    const need = Math.max(out, short);
    // "N i nød" var en gåde (IGN-løkke runde 3): nu står det, hvad der er i nød.
    setText(this.trustLabel, need > 0 ? `${plural(need, 'by', 'byer')} i nød` : 'Tillid');
    setAttr(this.trustLabel, 'title', need > 0 ? `${plural(need, 'by', 'byer')} mangler strøm. En by, der går i sort, koster et hjerte.` : null);
    toggle(this.trustLabel, 'warn', need > 0);
    // Tillid som hjerter, og som tekst til skærmlæseren.
    // Aldrig færre hjerter end tilliden: et hjerte, der ikke kan ses, kan heller ikke mistes.
    const max = Math.max(trustMax(s), s.trust);
    const key = `${s.trust}/${max}`;
    if (key !== this.heartsKey) {
      this.heartsKey = key;
      let html = '';
      for (let i = 0; i < max; i++) html += `<span class="heart ${i < s.trust ? 'on' : 'off'}">${i < s.trust ? ICON.heart : ICON.heartOff}</span>`;
      this.hearts.innerHTML = html;
      setAttr(this.hearts, 'aria-label', `Tillid ${s.trust} af ${max}`);
      setAttr(this.hearts, 'role', 'img');
      toggle(this.hearts.parentElement?.parentElement ?? this.hearts, 'low', s.trust <= 1);
      this.riskKey = '';
    }
    // Blinker en by med hjerte over taget, blinker det hjerte, der ryger, også her (spor "sandhed").
    let risk = 0;
    for (const n of activeCities(s)) if (cityLook(s, n).alarm) risk++;
    const rk = `${risk > 0}`;
    if (rk !== this.riskKey) {
      this.riskKey = rk;
      const on = this.hearts.querySelectorAll('.heart.on');
      on.forEach((el, i) => toggle(el as HTMLElement, 'risk', risk > 0 && i === on.length - 1));
      setAttr(this.hearts, 'aria-label', risk > 0 ? `Tillid ${s.trust} af ${max}. Et hjerte er i fare.` : `Tillid ${s.trust} af ${max}`);
    }
    // Fart og pause.
    // Under spillets egen korte pause viser knappen stadig pause: et tryk holder pausen (IGN runde 1).
    const paused = game.paused && !game.softPause;
    // IGN-løkke 2, runde 3: står uret stille, fordi et kort venter på svar, siger knappen det. Før stod
    // den på "spiller", mens alt stod stille bag et bykort.
    const card = !paused && !game.softPause && game.cardHold;
    // Makkerrummets nye år (Spor: tillid): knappen er et "klar", og den pulserer, til du har trykket.
    const year = paused && !!game.yearWait;
    const meReady = year && !!game.yearWait?.ready.includes(game.player);
    const pk = year ? (meReady ? 'year-ok' : 'year') : paused ? 'play' : game.softPause ? 'hold' : card ? 'card' : 'pause';
    if (this.pauseBtn.dataset.k !== pk) {
      this.pauseBtn.dataset.k = pk;
      this.pauseBtn.innerHTML = paused ? ICON.play : ICON.pause;
      const label = year ? (meReady ? 'Du er klar. Venter på makker' : 'Klar til det nye år') : paused ? 'Fortsæt' : game.softPause ? 'Hold pausen' : card ? 'Tiden står stille: kortet venter på svar' : 'Pause';
      setAttr(this.pauseBtn, 'aria-label', label);
      setAttr(this.pauseBtn, 'title', year ? `${label} (P eller mellemrum)` : paused ? 'Fortsæt (P eller mellemrum)' : game.softPause ? 'Hold pausen (P eller mellemrum)' : card ? 'Tiden står stille, til kortet er besvaret' : 'Pause (P eller mellemrum)');
    }
    toggle(this.pauseBtn, 'on', paused && !year);
    // IGN-løkke runde 3: ved spillets egen pause skiftede knappen ikke, så pausen var usynlig. Nu lyser
    // den gult og pulserer, til spillet kører igen (et tryk holder stadig pausen). Kortets pause ligeså.
    toggle(this.pauseBtn, 'hold', (!paused && (game.softPause || card)) || (year && !meReady));
    const spol = game.spol.active && !paused && s.phase === 'playing' && !game.remote;
    const sk = `${game.speed}${spol ? 'a' : ''}`;
    if (this.speedBtn.dataset.k !== sk) {
      this.speedBtn.dataset.k = sk;
      this.speedBtn.innerHTML = `${ICON.fast}<span>${game.speed}x</span>`;
      this.speedBtn.append(this.spolPill);
      this.spolPill.hidden = !spol;
      toggle(this.speedBtn, 'spol', spol);
      setAttr(this.speedBtn, 'aria-pressed', String(game.speed === 2));
      setAttr(this.speedBtn, 'aria-label', spol ? 'Dobbelt fart. Stille lige nu, så tiden går tre gange så hurtigt' : 'Dobbelt fart');
      setAttr(this.speedBtn, 'title', spol ? 'Stille lige nu: tiden går ×3, til der sker noget (F: dobbelt fart)' : 'Dobbelt fart (F)');
    }
    // Lad AI spille: robotten med "AI" ved siden af; tændt (petrol), mens AI'en har roret.
    this.aiBtn.hidden = !this.aiCan && !this.aiOn;
    const ak = this.aiOn ? 'on' : 'off';
    if (this.aiBtn.dataset.k !== ak) {
      this.aiBtn.dataset.k = ak;
      this.aiBtn.innerHTML = `${ICON.robot}<span>AI</span>`;
      setAttr(this.aiBtn, 'aria-pressed', String(this.aiOn));
      setAttr(this.aiBtn, 'aria-label', this.aiOn ? 'Tag over fra AI' : 'Lad AI spille');
      setAttr(this.aiBtn, 'title', this.aiOn ? 'Tag over (A)' : 'Lad AI spille (A): se den spille, og tag over når du vil');
    }
    const mk = muted ? 'm' : 's';
    if (this.muteBtn.dataset.k !== mk) {
      this.muteBtn.dataset.k = mk;
      this.muteBtn.innerHTML = muted ? ICON.muted : ICON.sound;
      setAttr(this.muteBtn, 'aria-label', muted ? 'Slå lyden til' : 'Slå lyden fra');
    }
    // Afslut året tidligt for en bonus. Knappen dukker først op, når årets hændelser og nye byer
    // er spillet (sim'en siger not_needed før), så den aldrig springer banens indhold over.
    const bonus = s.phase === 'playing' && !game.held ? earlyBonusPct(s) : 0;
    // Er nettet uroligt, står knappen gråtonet, så den ikke blinker ind og ud, og et tryk forklarer hvorfor.
    const c = check(s, 'call_year', undefined, undefined);
    const busy = !c.ok && c.error === 'busy';
    // Under en storm hører knappen ikke hjemme: den forsvinder helt, til uvejret er drevet over.
    const storm = s.threats.some((t) => t.kind === 'storm');
    const canCall = bonus > 0 && (c.ok || (busy && !storm));
    this.callBtn.hidden = !canCall;
    this.callShown = canCall && !busy;
    if (canCall) {
      // Kort etiket, så knappen kan være der på en 390 px skærm; bonussen står i et lille mærke
      // på hjørnet og i værktøjstippet.
      const ck = String(bonus);
      if (this.callBtn.dataset.k !== ck) {
        this.callBtn.dataset.k = ck;
        this.callBtn.innerHTML = `<span>Næste år</span>${ICON.fast}<small class="call-bonus" aria-hidden="true">+${bonus} %</small>`;
      }
      setAttr(this.callBtn, 'title', busy ? 'Vent, til nettet er roligt igen' : `Start næste år nu, og få ${bonus} % mere ved årsskiftet (N)`);
      setAttr(this.callBtn, 'aria-disabled', String(busy));
      setAttr(this.callBtn, 'aria-label', `Start næste år nu og få ${bonus} procent mere i indtægt`);
    }
  }

  /** Står Næste år-knappen klar lige nu (til en forklarende toast første gang)? */
  callShown = false;

  /** Sekunder til årsskiftet (til hint). */
  static secondsLeft(game: Game): number {
    return Math.max(0, Math.ceil((YEAR_TICKS - (game.state.clockTick % YEAR_TICKS)) / TICK_HZ));
  }
}

function activeCities(s: State) {
  return s.nodes.filter((n) => n.active && !n.phantom && n.kind === 'city' && n.city);
}

/**
 * Antal aktive byer, der mangler strøm lige nu (gået i sort, eller mørkeuret kører og byen får under
 * tærsklen). IGN-løkke 2 (runde 2): "1 by i nød", mens alle byer var forsynet. Et ur, der er ved at
 * fylde op igen efter en ny ledning, er ikke nød; samme regel som bykortet og hintet (isPowered).
 */
export function starving(s: State): number {
  let k = 0;
  for (const n of activeCities(s)) if (n.city && (n.city.out || (n.city.clock > 0 && !isPowered(n)))) k++;
  return k;
}

/** Alle aktive byer får hele deres behov dækket. */
export function allLit(s: State): boolean {
  const cs = activeCities(s);
  return cs.length > 0 && cs.every((n) => n.city !== undefined && !n.city.out && n.city.servedMw >= n.city.demandMw);
}
